/**
 * Inline-SVG Zeus-Blitz icon. Pure synchronous render — no async PNG load,
 * no Suspense risk. Color is fully parameterized via CSS variables on the
 * three semantic SVG elements (bolt body, grip, outline accent).
 *
 * Style references the look of the source pixel-art: diagonal zigzag with a
 * grey grip in the middle and a glowing aura. Tuned to read at sizes from
 * ~1.2 rem (inventory list) up to ~6 rem (item-detail-modal).
 */

interface Props {
  /** Container size in rem. */
  sizeRem?: number;
  /** Optional class on outer wrapper. */
  className?: string;
  /** Bolt body color. Default: gold. */
  boltColor?: string;
  /** Highlight color for the inner zigzag stroke. Auto-derived if omitted. */
  highlightColor?: string;
  /** Grip band color (middle of bolt). */
  gripColor?: string;
  /** Outline (dark stroke around the bolt shape). */
  outlineColor?: string;
  /** Add a soft drop-shadow glow around the bolt. */
  glow?: boolean;
  /** Override the glow color (defaults to boltColor). */
  glowColor?: string;
}

/** Lerp a hex color toward white by amount (0..1). */
function lighten(hex: string, amount: number): string {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex.trim());
  if (!m) return hex;
  const r = parseInt(m[1], 16);
  const g = parseInt(m[2], 16);
  const b = parseInt(m[3], 16);
  const lr = Math.round(r + (255 - r) * amount);
  const lg = Math.round(g + (255 - g) * amount);
  const lb = Math.round(b + (255 - b) * amount);
  return `rgb(${lr}, ${lg}, ${lb})`;
}

// Zigzag path coordinates (viewBox 100×100).
// The path runs from upper-left to lower-right, kinking back and forth.
// Width is set via stroke-width on a polyline; the grip is overlaid in the middle.
const BOLT_POINTS = '15,12 28,28 18,38 38,52 28,62 50,76 42,86 60,88';

export function ZeusBoltSprite({
  sizeRem = 3,
  className = '',
  boltColor = '#ffd84a',
  highlightColor,
  gripColor = '#9ea4ad',
  outlineColor = '#0d0d0d',
  glow = true,
  glowColor,
}: Props) {
  const effectiveHighlight = highlightColor ?? lighten(boltColor, 0.55);
  const effectiveGlow = glowColor ?? boltColor;

  return (
    <div
      className={className}
      style={{
        width: `${sizeRem}rem`,
        height: `${sizeRem}rem`,
        display: 'grid',
        placeItems: 'center',
        filter: glow ? `drop-shadow(0 0 ${sizeRem * 0.18}rem ${effectiveGlow})` : undefined,
      }}
      aria-hidden="true"
    >
      <svg
        viewBox="0 0 100 100"
        width="100%"
        height="100%"
        style={{ shapeRendering: 'crispEdges' }}
      >
        {/* Outer dark stroke for the pixel-art outline feel */}
        <polyline
          points={BOLT_POINTS}
          fill="none"
          stroke={outlineColor}
          strokeWidth={14}
          strokeLinecap="square"
          strokeLinejoin="miter"
        />
        {/* Main bolt body */}
        <polyline
          points={BOLT_POINTS}
          fill="none"
          stroke={boltColor}
          strokeWidth={9}
          strokeLinecap="square"
          strokeLinejoin="miter"
        />
        {/* Inner highlight stripe — slimmer, brighter */}
        <polyline
          points={BOLT_POINTS}
          fill="none"
          stroke={effectiveHighlight}
          strokeWidth={3}
          strokeLinecap="square"
          strokeLinejoin="miter"
          opacity={0.75}
        />
        {/* Grip band in the middle of the bolt (rotated rectangle) */}
        <g transform="translate(50 50) rotate(50)">
          <rect x={-7} y={-2.5} width={14} height={5} fill={outlineColor} />
          <rect x={-6} y={-1.5} width={12} height={3} fill={gripColor} />
        </g>
      </svg>
    </div>
  );
}
